import React, { useContext, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import CircularProgressFixed from '../../components/utils/CircularProgressFixed';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import AIToolsTopBarContent from '../../components/AIAssistant/Page/AIToolsTopBarContent';
import AIToolsPageContent from '../../components/AIAssistant/Page/AIToolsPageContent';
import { backgroundGradients, palette, spaces } from '../../assets/styles/themes/tokens';
import {
  aiAssistantContext,
  AIAssistantDisplayMode,
  PodcastDetailsPendingReason,
} from '../../state/AIAssistantContextProvider';
import { ActiveView } from '../../components/AIAssistant/shared/constants';
import AIAssistantChat from '../../components/AIAssistant/shared/Chat/AIAssistantChat';
import PageTemplatePromptForm from '../../components/AIAssistant/Page/PageTemplatePromptForm';
import PodcastLoadingView from '../../components/AIAssistant/Page/PodcastLoadingView';
import { AIAssistantAlerts } from '../../components/AIAssistant/shared/AIAssistantAlerts';
import AIAssistantViewRouter, { type ViewMap } from '../../components/AIAssistant/shared/AIAssistantViewRouter';
import AIAssistantEditorProvider, { aiAssistantEditorContext } from '../../state/AIAssistantEditorProvider';
import { useSelector } from 'react-redux';
import {
  getConversation as getConversationSelector,
  getCurrentConversationId,
  getAiGeneratedImagesRemainingLimit,
  getAiGeneratedImagesRemainingLimitStatus,
} from '../../reducers/aiAssistant.selectors';
import { selectUser } from '../../reducers/user.selectors';
import useTemplateSelection from '../../components/AIAssistant/shared/useTemplateSelection';
import { isImageGenerationTemplate } from '../../components/AIAssistant/shared/utils';
import {
  parseLaunchParams,
  isLaunchTemplateEntitled,
} from '../../components/AIAssistant/shared/templateLaunchParams';
import useScreenSize from '../../utils/useScreenSize';
import useRouter from '../../utils/useRouter';
import { 
  setCurrentConversationId as setCurrentConversationIdAction 
} from '../../actions/aiAssistant.actions';
import useAction from '../../utils/useAction';
import { useMediaQuery } from '@mui/material';
import mainTheme from '../../assets/styles/themes/mainTheme';
import useRedirectIfNoAIAssistantAccess from '../../utils/useRedirectIfNoAIAssistantAccess';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { routes } from '../../constants';

const pageContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  background: backgroundGradients.aiAssistant,
  position: 'relative',
  overflowX: 'hidden',
  boxSizing: 'border-box',
};

// `ai-tools-page` is itself a scroll container: overflowX: 'hidden' makes overflow-y
// compute to 'auto', so absolutely positioned children of it scroll away with the
// content. Content-area overlays are anchored here instead — same box, never scrolls.
// `clip` rather than `hidden` because `hidden` would itself be a scroll container.
const contentAreaStyles = {
  position: 'relative',
  height: '100%',
  overflow: 'clip',
};

const breadcrumbBarStyles = {
  display: 'flex',
  padding: `${spaces.sm} ${spaces.md}`,
  alignItems: 'center',
  gap: spaces.xs,
  alignSelf: 'stretch',
  borderBottom: `1px solid ${palette.divider}`,
  background: palette.background.paperElevation2,
  position: 'sticky',
  top: 0,
};

const PAGE_VIEW_MAP: ViewMap = {
  [ActiveView.FREE_DRAFT_ENTRY]: AIToolsPageContent,
  [ActiveView.CHAT]: AIAssistantChat,
  [ActiveView.TEMPLATE_PROMPT_FORM]: PageTemplatePromptForm,
  [ActiveView.PODCAST_LOADING]: PodcastLoadingView,
};

// Behind enable-podcast-v1-ux-flag the loading state renders inside the
// AIAssistantChat two-pane shell (loading view left, chat messages right)
// instead of the standalone full-screen loader.
const PODCAST_V1_UX_PAGE_VIEW_MAP: ViewMap = {
  ...PAGE_VIEW_MAP,
  [ActiveView.PODCAST_LOADING]: AIAssistantChat,
};

// The podcast v1 split shell only applies on non-small screens; small screens
// keep the standalone loading view (SwipeableDrawer-based, section 7 specifics).
export const selectPageViewMap = (isPodcastV1UxEnabled: boolean, isSmallScreen: boolean): ViewMap =>
  isPodcastV1UxEnabled && !isSmallScreen ? PODCAST_V1_UX_PAGE_VIEW_MAP : PAGE_VIEW_MAP;

const AIToolsPageContentWrapper: React.FC = () => {
  const { activeView, displayMode, pendingPodcastDetailsReason, isPodcastEntryStatusPending, isPodcastV1UxEnabled,
    templateLaunchInProgressRef } = useContext(aiAssistantContext);
  const { isTextEditorOpen } = useContext(aiAssistantEditorContext);
  const { isLoading, hasAccess } = useRedirectIfNoAIAssistantAccess();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;
  const isXlScreen = useMediaQuery(mainTheme.breakpoints.up('xl'), { noSsr: true });
  const { router, match } = useRouter();
  const urlConversationId = match.params.conversationId;
  const setCurrentConversationId = useAction(setCurrentConversationIdAction);
  const urlConversationState = useSelector((state) =>
    getConversationSelector(state, urlConversationId ?? null)
  );
  useEffect(() => {
    if (urlConversationId && urlConversationState?.status === AsyncActionStatus.ERROR) {
      setCurrentConversationId({ conversationId: null });
      router.replace(`/${routes.aiTools.root}`);
    }
  }, [
    urlConversationId,
    urlConversationState?.status,
    router,
    setCurrentConversationId,
  ]);

  const user = useSelector(selectUser);
  const currentConversationId = useSelector(getCurrentConversationId);
  const imagesRemainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
  const imagesRemainingLimitStatus = useSelector(getAiGeneratedImagesRemainingLimitStatus);
  const { selectTemplate } = useTemplateSelection();
  const launchConsumedRef = useRef(false);
  const launchQuery = match.location?.query;

  // Consume an allowlisted dashboard launch link once on mount: pre-select the
  // template + seed the form, then strip the query so reload/back doesn't re-fire.
  // Anything invalid, unentitled, or arriving while a conversation/editor is active
  // degrades silently to the normal landing.
  useEffect(() => {
    if (launchConsumedRef.current) return;
    if (urlConversationId || currentConversationId || isTextEditorOpen) return;
    // Wait for the user before evaluating entitlement — while `me` is loading,
    // hasAccess is optimistically true and `user` is undefined, so a school user
    // would otherwise slip past the gate on a cold-load / new-tab launch.
    if (isLoading || !user) return;

    const intent = parseLaunchParams(launchQuery);
    if (!intent) return;

    const cleanUrl = `/${routes.aiTools.root}`;

    if (!isLaunchTemplateEntitled(intent.template, !!user.isSchoolUser)) {
      launchConsumedRef.current = true;
      router.replace(cleanUrl);
      return;
    }

    // Image templates gate on a remaining-limit that loads asynchronously; wait for
    // it so the launch doesn't spuriously trip the image-limit alert on a cold mount.
    // A terminal ERROR means the fetch won't resolve — stop waiting and consume the
    // launch anyway (limit stays null → no false alert) rather than stranding the card.
    if (
      isImageGenerationTemplate(intent.template) &&
      imagesRemainingLimit === null &&
      imagesRemainingLimitStatus !== AsyncActionStatus.ERROR
    ) return;

    launchConsumedRef.current = true;
    templateLaunchInProgressRef.current = true;
    selectTemplate(intent.template, intent.prefill);
    router.replace(cleanUrl);
  }, [
    urlConversationId,
    currentConversationId,
    isTextEditorOpen,
    isLoading,
    imagesRemainingLimit,
    imagesRemainingLimitStatus,
    launchQuery,
    user,
    router,
    selectTemplate,
    templateLaunchInProgressRef,
  ]);

  const shouldCollapseSidebar = 
    isTextEditorOpen && displayMode === AIAssistantDisplayMode.PAGE && !isSmallScreen && !isXlScreen;

  const showBreadcrumbsBar = isSmallScreen;

  if (isLoading || !hasAccess) {
    return null;
  }

  return (
    <MasterLayout 
      customTopBarContent={!isSmallScreen && <AIToolsTopBarContent />}
      sidebarCollapsed={shouldCollapseSidebar}
    >
      <Box sx={contentAreaStyles} data-testid='ai-tools-content-area'>
        <AIAssistantAlerts />
        <Box sx={pageContainerStyles} data-testid='ai-tools-page'>
          {showBreadcrumbsBar && (
            <Box sx={(theme) => ({ ...breadcrumbBarStyles, zIndex: theme.zIndex.appBar })}>
              <AIToolsTopBarContent />
            </Box>
          )}
          {pendingPodcastDetailsReason === PodcastDetailsPendingReason.PAGE_ENTRY || isPodcastEntryStatusPending ? (
            <Box
              data-testid='podcast-details-pending-loader'
              sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <CircularProgressFixed />
            </Box>
          ) : (
            <AIAssistantViewRouter
              activeView={activeView}
              viewMap={selectPageViewMap(isPodcastV1UxEnabled, isSmallScreen)}
            />
          )}
        </Box>
      </Box>
    </MasterLayout>
  );
};

const AIToolsPage: React.FC = () => {
  return (
    <AIAssistantEditorProvider>
      <AIToolsPageContentWrapper />
    </AIAssistantEditorProvider>
  );
};

export default AIToolsPage;

